import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button';
import StripeLogo from "assets/images/Stripe-Emblem.png";
import SoftAvatar from "components/SoftAvatar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useAgencyInfo } from "context/agency";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";
import { useUpdateAgencyStripe, useUpdateAgency } from "queries/agency";
import { useEffect, useState } from "react";
import env from "../../../../../../config/env";
import Success from "../Success";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const { GET, POST } = ReqMethods;

function Accounts({ data }) {
  const [agency, Update] = useAgencyInfo()
  const request = useRequest();
  // The Stripe OAuth round trip leaves this iframe for a top-level tab, so
  // localforage (partitioned by browser storage rules for third-party
  // iframes) isn't reliably readable when /integrations/stripe loads back
  // up. Fetch a short-lived, agency-scoped connect token here (while the
  // real session is still verifiable) and carry THAT through `state`
  // instead of the session token itself - /agency/stripe/complete-connect
  // verifies it server-side and derives agency_id from it.
  const { data: connectTokenData } = useQuery({
    queryKey: ["stripeConnectToken", agency?._id],
    queryFn: () => request(GET, "/agency/stripe/connect-token", {
      query: `agency_id=${agency._id}`,
    }),
    enabled: !!agency?._id,
  });
  const connectToken = connectTokenData?.response?.data?.token;
  const queryClient = useQueryClient()
  const [slack2FA, setSlack2FA] = useState(true);
  const [spotify2FA, setSpotify2FA] = useState(true);
  const [atlassian2FA, setAtlassian2FA] = useState(true);
  const [asana2FA, setAsana2FA] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uri, setURI] = useState('')
  const handleSetSlack2FA = () => setSlack2FA(!slack2FA);
  const handleSetSpotify2FA = () => setSpotify2FA(!spotify2FA);
  const handleSetAtlassian2FA = () => setAtlassian2FA(!atlassian2FA);
  const handleSetAsana2FA = () => setAsana2FA(!asana2FA);

  // All five mutations below touch the agency's Stripe/SaaS integration
  // config, so they all refresh the shared ["agency"] context on success.
  const invalidateAgency = () => queryClient.invalidateQueries({ queryKey: ["agency"] });

  const updateStripeMutation = useUpdateAgencyStripe();
  // "saas" is another fork's domain (queries/saas.js) - inlined here via
  // useRequest() rather than centralized.
  const saasSubscriptionMutation = useMutation({
    mutationFn: (vars) => request(GET, "/saas/subscriptions", vars),
    onSuccess: ({ response }) => { if (response) invalidateAgency(); },
  });
  const deleteStripeMutation = useMutation({
    mutationFn: (vars) => request(GET, "/saas/deletestripfromagency", vars),
    onSuccess: ({ response }) => { if (response) invalidateAgency(); },
  });
  const saveStripKeyMutation = useMutation({
    mutationFn: (payload) => request(POST, "/saas/stripkeysave", { payload }),
    onSuccess: ({ response }) => { if (response) invalidateAgency(); },
  });
  const updateApiKeyMutation = useUpdateAgency();

  const handleSave = async (e) => {
    e.preventDefault();
    setProcessing(true)
    const payload = {
      _id: agency._id,
      stripe: {
        key: e.target.stripe_key.value
      },
    };

    const { response } = await updateStripeMutation.mutateAsync(payload);
    if (response) {
      Update(response.data);
      setProcessing(false)
    } else {
      setProcessing(false)

    }
  }
  const handleSaas = async (e) => {
    setProcessing(true)
    const { response } = await saasSubscriptionMutation.mutateAsync({
      query: `agency_id=${agency?._id}`,
      payload: true
    });
    if (response) {
      setProcessing(false)
    } else {
      setProcessing(false)

    }
  }

  const [open, setOpen] = useState(false);

  // Handlers to open and close the modal
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Function to handle the delete action
  const handleDelete = () => {
    console.log("Delete action triggered");
    setOpen(false);
  };

  const deletefromagency = async () => {
    setProcessing(true)
    const { response } = await deleteStripeMutation.mutateAsync({
      query: `agency_id=${agency?._id}`,
      payload: true
    });
    if (response) {
      handleClose();
      setProcessing(false)
    } else {
      handleClose();
      setProcessing(false)

    }

  }
  const [stripeKey, setStripeKey] = useState(agency?.stripe?.secret_key);
  const handleInputChange = (e) => {
    setStripeKey(e.target.value);
  };
  const saveStripKey = async (e) => {
    if (!stripeKey) {
      alert("please Enter Key first")
    }
    setProcessing(true);
    const payload = {
      agency_id: agency?._id,
      stripkey: stripeKey
    }
    const { response } = await saveStripKeyMutation.mutateAsync(payload);
    if (response) {
      setProcessing(false)
    } else {
      setProcessing(false)
    }
  }

  // API key state + handlers for display/copy/create
  const [apiKey, setApiKey] = useState(agency?.apiKey || "");
  const [showApiKey, setShowApiKey] = useState(false);

  // keep local apiKey in sync when agency changes
  useEffect(() => {
    setApiKey(agency?.apiKey || "");
  }, [agency]);

  const handleCopy = async () => {
    try {
      if (apiKey) {
        await navigator.clipboard.writeText(apiKey);
        // optional: show toast or feedback
        console.log("API key copied to clipboard");
      }
    } catch (err) {
      console.error("Failed to copy API key", err);
    }
  };

  const handleToggleVisibility = () => {
    setShowApiKey((s) => !s);
  };

  const handleCreateApiKey = () => {
    // generate a key in the format: 8hex-8hex-8hex (e.g. f7e47e1b-d133e4b9-c9bd40b9)
    const randomHex = (len) => {
      let s = "";
      for (let i = 0; i < len; i++) {
        s += Math.floor(Math.random() * 16).toString(16);
      }
      return s;
    };
    const newKey = `${randomHex(8)}-${randomHex(8)}-${randomHex(8)}`;
    setApiKey(newKey);
    // persist new key to agency
    saveApiKey(newKey);
  };

  // Persist API key to the agency via PUT /agency.
  // Try several possible endpoints (best-effort) and update context on success.
  const saveApiKey = async (key) => {
    if (!agency?._id) return;
    setProcessing(true);
    const payload = { _id: agency._id, apiKey: key };
    try {
      let result;

      result = await updateApiKeyMutation.mutateAsync(payload);

      const { response } = result || {};
      if (response) {
        Update(response.data);
      }
    } catch (err) {
      console.error('Failed to save API key', err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>

      <div className="equalheight d-flex w-100 " style={{
        border: "1px solid rgba(229, 229, 229, 1)",
        background: "white",
        borderRadius: "5px",

      }}    >
        <div className="d-flex p-20-px text-black text-bold text-left w-50-per">
          <SoftAvatar src={StripeLogo} alt="Slack logo" variant="rounded" />
          <SoftBox ml={2} lineHeight={0}>
            <SoftTypography variant="h5" fontWeight="medium">
              Stripe Integration
            </SoftTypography>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              Integrate your Stripe Account
            </SoftTypography>
          </SoftBox>
        </div>
        <div className="p-20-px text-black text-bold text-right w-50-per">
          {agency?.stripe && (
            <Success action={handleSaas} text1={"Your Request Has Been Submitted!"} text2={"It Will Take A little Time!"} />
          )}
          <button className="btn-new-design-black-addacount font-16" style={{ margin: "0px 0px 0px 10px", padding: "10px 20px", border: "green", color: "green" }} disabled={processing || !connectToken} loading={processing} type="submit" variant="outlined" color={agency?.stripe ? "success" : "dark"} size="small">
            <a style={{ color: "inherit" }} rel="noreferrer" target="_blank" href={connectToken ? `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${env.STRIPE_CLIENT_ID}&scope=read_write&state=${connectToken}&redirect_uri=${env.REDIRECT_STRIPE}` : undefined} disabled={processing || !connectToken} loading={processing}>{data.stripe ? "Connected" : "Connect"}</a>
          </button>
          {data.stripe &&
            <button onClick={handleOpen} className="btn-new-design-black-addacount font-16" style={{ margin: "0px 0px 0px 10px", padding: "10px 20px", border: "red", color: "red" }} type="submit" variant="outlined" color={agency?.stripe ? "dark" : "success"} size="small">
              {data.stripe && "Disconnect"}
            </button>
          }

          <div className="flex">
            <input

              type="text"
              name="stripkey"
              style={{ width: "70%", paddingLeft: "5px", margin: "10px", borderRadius: "5px" }}
              placeholder="Enter Stripe Key"
              value={stripeKey}
              onChange={handleInputChange}
            />
            <button className="btn-new-design-black-addacount font-16" onClick={saveStripKey} disabled={processing} loading={processing}>Save</button>
          </div>

          {/* Modal Component */}
          {open &&
            <div className="modal-zz-confirm text-left">
              <div className="scrollable-modal-content bg-white rounded" style={{ position: "relative" }}>
                <h2 className='text-black text-left font-14'> Confirm Action </h2>
                <span className='crossison' onClick={handleClose}>X</span>
                <p>Are you sure you want to disconnect?</p>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '20px' }}>
                  <Button variant="dark" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button variant="dark" color="red" onClick={deletefromagency}>
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          }
          {/* <SoftBox mr={1} display="flex" gap={2}
              alignItems="center"
              component="form" onSubmit={handleSave}>
              <FormField
                label="Enter Stripe Key"
                name="stripe_key"
                defaultValue={agency?.stripe?.key}
              />
              <SoftBox>

                <SoftButton sx={{ mt: 2 }} disabled={processing} loading={processing} type="submit" variant="outlined" color="dark" size="small">
                  Save
                </SoftButton>
              </SoftBox>
            </SoftBox> */}

        </div >


      </div >

      <div className="" style={{ marginTop: 16, width: '100%' }}>
        <h2 className="" style={{ fontWeight: "bold", marginBottom: 8 }}>API Key</h2>
        <p className="pt-3 mb-4" style={{ marginTop: 0, marginBottom: 12, color: '#666' }}>
          Use this API key to access our services programmatically.
        </p>

        <div className="!border-[#D9D9D9] rounded flex" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>

          <div className="flex items-center w-[556px] h-[55px] pl-4 bg-[#F5F5F5] !border-[#D9D9D9] rounded border p-1" style={{ display: 'flex', alignItems: 'center', width: 556, height: 55, paddingLeft: 16, background: '#F5F5F5', borderRadius: 8 }}>
            <div className="w-[415px]" style={{ marginRight: 20, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {showApiKey || typeof apiKey !== 'string' ? (
                apiKey
              ) : (
                Array.from(apiKey).map((_, idx) => (
                  <FiberManualRecordRoundedIcon key={idx} style={{ fontSize: 'small' }} />
                ))
              )}
            </div>

            <button className="ms-6 py-2 text-xs  text-[#757575] rounded-md uppercase font-bold" onClick={handleCopy} aria-label="Copy API Key" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <FileCopyOutlinedIcon fontSize="small" />
            </button>
            <button className="px-2 ms-2 py-2 text-xs  text-[#757575] rounded-md uppercase font-bold" onClick={handleToggleVisibility} aria-label="Toggle API Key visibility" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
              {showApiKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </button>
          </div>

          <button className="px-10 ml-4 py-2 text-xs bg-black text-white rounded-md uppercase font-bold" onClick={handleCreateApiKey} style={{ padding: '10px 24px', background: '#000', color: '#fff', borderRadius: 6 }}>
            Generate New Key
          </button>
        </div>
      </div>
    </>
  );
}

export default Accounts;
